import React, { useEffect, useState } from 'react'
import { usePaginatedQuery } from 'react-query'
import { getDownloads } from '../../Api'
import arrowLeft from '../SearchResults/images/arrowLeft.svg'
import arrowRight from '../SearchResults/images/arrowRight.svg'
import expandDownloadIcon from '../SearchResults/images/expandedSVG.svg'
import SingleDownload from './SingleDownload/SingleDownload'
import styles from './styles.module.css'
import JSZip from 'jszip'

function AllDownloads () {
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [downloadRecord, setDownloadRecord] = useState()
  const { resolvedData } = usePaginatedQuery([page, limit], getDownloads)

  const [isSingleDownloadVisible, setIsSingleDownloadVisible] = useState(false)
  const [activeDownloadRecord, setActiveDownloadRecord] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [filesRemaining, setFilesRemaining] = useState(0)

  useEffect(() => {
    document.title = 'Datafiniti Portal | Settings'
    localStorage.setItem('dfp/dataTypeNavigation', 'download')
    window.scrollTo(0, 0)
  }, []) /* eslint-disable-line react-hooks/exhaustive-deps */

  const toggleSingleDownload = id => {
    setDownloadRecord(id)
    setIsSingleDownloadVisible(true)
  }

  const capitalize = str => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''
  }

  const parseQueryToFilters = query => {
    const filterPattern = /([\w.]+):((?:"[^"]+"|\([^)]+\)|\[[^\]]+\]|[^ }\]]+))/g
    const parsedFilters = []
    let match

    while ((match = filterPattern.exec(query)) !== null) {
      let key = match[1]
      let value = match[2]

      if (value.startsWith('(') && value.endsWith(')')) {
        value = value.slice(1, -1)
        value = value
          .split(' OR ')
          .map(segment => segment.replace(/^"|"$/g, ''))
          .join(' OR ')
      } else {
        value = value.replace(/^"|"$/g, '')
      }

      if (value.endsWith(')')) {
        value = value.slice(0, -1)
      }

      parsedFilters.push({ key, value })
    }

    return parsedFilters
  }

  const handleDownloadAll = async (event, recordId, urls) => {
    event.stopPropagation()
    if (!urls || urls.length === 0) return

    setActiveDownloadRecord(recordId)
    setIsDownloading(true)
    setFilesRemaining(urls.length)

    const zip = new JSZip()

    for (let i = 0; i < urls.length; i++) {
      const fileUrl = urls[i].url ?? urls[i]
      try {
        const res = await fetch(fileUrl)
        const data = await res.arrayBuffer()
        const rawName = new URL(fileUrl).pathname.split('/').pop()
        const name = rawName || `file-${i + 1}`
        zip.file(name, data)
      } catch (err) {
        console.error('Failed to fetch', fileUrl, err)
      }
      setFilesRemaining(prev => prev - 1)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(zipBlob)
    link.download = `download-${recordId}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)

    setIsDownloading(false)
    setActiveDownloadRecord(null)
  }

  const handlePreviousPage = () => {
    if (page > 1) setPage(prev => prev - 1)
  }

  const handleNextPage = () => {
    const totalPages = Math.ceil(resolvedData?.data?.num_found / limit)
    if (page < totalPages) setPage(prev => prev + 1)
  }

  return (
    <section className={styles.sectionContainerAllDownloads}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h1 className={styles.allDownloadsTitle}>Downloads</h1>
        {resolvedData?.data?.records?.length > 0 && (
          <div style={{ display: 'flex' }}>
            <span
              style={{
                marginRight: '16px',
                fontSize: '12px',
                fontWeight: '400',
                paddingTop: '2px'
              }}
            >
              {`${(page - 1) * limit + 1}-${Math.min(
                page * limit,
                resolvedData?.data?.num_found || 0
              )} of ${resolvedData?.data?.num_found}`}
            </span>
            <div style={{ paddingTop: '1px' }}>
              <img
                onClick={page > 1 ? handlePreviousPage : null}
                src={arrowLeft}
                alt='Previous'
                style={{
                  marginRight: '8px',
                  height: '14px',
                  width: '14px',
                  cursor: page > 1 ? 'pointer' : 'not-allowed',
                  opacity: page > 1 ? 1 : 0.5
                }}
              />
              <img
                onClick={
                  page < Math.ceil(resolvedData?.data?.num_found / limit)
                    ? handleNextPage
                    : null
                }
                src={arrowRight}
                alt='Next Page'
                style={{
                  height: '14px',
                  width: '14px',
                  cursor:
                    page < Math.ceil(resolvedData?.data?.num_found / limit)
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    page < Math.ceil(resolvedData?.data?.num_found / limit)
                      ? 1
                      : 0.5
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.containerAllDownloads}>
        {resolvedData?.data?.records.length > 0 ? (
          resolvedData?.data?.records.map((record, index) => (
            <article
              className={styles.articleDownloads}
              onClick={() => toggleSingleDownload(record.id)}
              key={index}
            >
              <header
                style={{
                  display: 'flex',
                  marginBottom: '12px',
                  justifyContent: 'space-between'
                }}
              >
                <h2 className={styles.allDownloadsHeaderTitle}>{record?.id}</h2>
                <img
                  style={{ cursor: 'pointer', height: '13px' }}
                  src={expandDownloadIcon}
                  alt='Expand Icon'
                />
              </header>

              <div className={styles.allDownloadsBox}>
                <div className={styles.colStatus}>
                  <h3 className={styles.titleH3AllDownloads}>Status</h3>
                  <span className={styles.spanAllDownloads}>
                    {record?.status ? capitalize(record.status) : 'N/A'}
                  </span>
                </div>

                <div className={styles.colDateStarted}>
                  <h3 className={styles.titleH3AllDownloads}>Date started</h3>
                  <span className={styles.spanAllDownloads}>
                    {record?.date_started
                      ? new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          timeZoneName: 'short',
                          hour12: false
                        }).format(new Date(record.date_started))
                      : 'N/A'}
                  </span>
                </div>

                <div className={styles.colDataType}>
                  <h3 className={styles.titleH3AllDownloads}>Data type</h3>
                  <span className={styles.spanAllDownloads}>
                    {record?.data_type
                      ? record.data_type.charAt(0).toUpperCase() +
                        record.data_type.slice(1).toLowerCase()
                      : 'N/A'}
                  </span>
                </div>

                <div
                  className={styles.colFilters}
                  style={{
                    minHeight: '125px',
                    overflow: 'auto',
                    maxHeight: '125px'
                  }}
                >
                  <h3 className={styles.titleH3AllDownloads}>Filters</h3>
                  {record?.query ? (
                    <>
                      {parseQueryToFilters(record.query)
                        .slice(0, 5)
                        .map((filter, idx) => (
                          <span
                            key={`${filter.key}-${idx}`}
                            style={{ marginBottom: '4px' }}
                            className={styles.spanAllDownloads}
                          >
                            {`${filter.key}: ${filter.value}`}
                          </span>
                        ))}
                      {parseQueryToFilters(record.query).length > 5 && (
                        <span
                          style={{ color: '#ABABAB' }}
                          className={styles.spanAllDownloads}
                        >
                          +{parseQueryToFilters(record.query).length - 5} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={styles.spanAllDownloads}>N/A</span>
                  )}
                </div>

                <div className={styles.colRecordsDownloaded}>
                  <h3 className={styles.titleH3AllDownloads}>
                    Records downloaded
                  </h3>
                  <span className={styles.spanAllDownloads}>
                    {record?.num_downloaded || 'N/A'}
                  </span>
                </div>

                <div className={styles.colFileFormat}>
                  <h3 className={styles.titleH3AllDownloads}>File format</h3>
                  <span className={styles.spanAllDownloads}>
                    {record?.format.toUpperCase() || 'N/A'}
                  </span>
                </div>

                <div className={styles.colDownloadAction}>
                  <h3 className={styles.titleH3AllDownloads}>Download</h3>
                  {record?.status === 'completed' ? (
                    <span
                      onClick={e => {
                        if (
                          !isDownloading ||
                          activeDownloadRecord === record.id
                        ) {
                          handleDownloadAll(e, record.id, record?.results || [])
                        }
                      }}
                      className={styles.spanDownloadFiles}
                      style={{
                        cursor:
                          isDownloading && activeDownloadRecord === record.id
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                    >
                      {isDownloading && activeDownloadRecord === record.id
                        ? `Downloading (${Math.max(filesRemaining, 0)} ${
                            Math.max(filesRemaining, 0) === 1 ? 'file' : 'files'
                          } left)`
                        : 'Download all files'}
                    </span>
                  ) : (
                    <span
                      className={styles.spanDownloadFiles}
                      style={{ cursor: 'pointer' }}
                    >
                      {record?.status === 'running'
                        ? 'Download running'
                        : record?.status === 'queued'
                        ? 'Download queued'
                        : record?.status
                        ? capitalize(record.status)
                        : 'Not available'}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))
        ) : (
          <div>
            <span>No downloads available</span>
          </div>
        )}
      </div>

      {isSingleDownloadVisible && (
        <SingleDownload
          downloadId={downloadRecord}
          onClose={() => setIsSingleDownloadVisible(false)}
          parseQueryToFilters={parseQueryToFilters}
        />
      )}
    </section>
  )
}

export default AllDownloads
